<div class="fiscal-reports">

  <!-- Auto Z-Report failure banner -->
  <nb-alert *ngIf="autoZReportFailed" status="warning" closable class="mb-4">
    <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
    <strong>Automatic Z-Report failed</strong> on {{ autoZReportLastRunAt | date:'dd/MM/yyyy HH:mm' }}:
    {{ autoZReportLastError }}. Please review and generate the Z-Report manually if needed.
  </nb-alert>

  <!-- Header -->
  <div class="row mb-4 align-items-center">
    <div class="col-md-8">
      <h4>Fiscal Reports</h4>
      <p class="text-muted">SKAT-compliant X-Report and Z-Report management</p>
    </div>
  </div>

  <!-- Sub-navigation -->
  <nb-card class="mb-4">
    <nb-card-body class="p-0">
      <nav class="nav nav-tabs sub-tabs">
        <a
          class="nav-link"
          [class.active]="activeSection === 'x-report'"
          (click)="setSection('x-report')"
          href="javascript:void(0)">
          <nb-icon icon="activity-outline" class="me-2"></nb-icon>
          X-Report (Live)
        </a>
        <a
          class="nav-link"
          [class.active]="activeSection === 'z-reports'"
          (click)="setSection('z-reports')"
          href="javascript:void(0)">
          <nb-icon icon="archive-outline" class="me-2"></nb-icon>
          Z-Reports (History)
          <span *ngIf="zTotal > 0" class="badge bg-secondary ms-2">{{ zTotal }}</span>
        </a>
      </nav>
    </nb-card-body>
  </nb-card>

  <!-- ===================== X-REPORT SECTION ===================== -->
  <div *ngIf="activeSection === 'x-report'">

    <!-- Actions -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-0">X-Report — Current Period Snapshot</h5>
        <small class="text-muted">Live data — period not yet closed</small>
        <span *ngIf="xReport?.periodStart" class="ms-3 text-muted small">
          Period start: {{ xReport.periodStart | date:'dd/MM/yyyy HH:mm' }}
        </span>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" (click)="loadXReport()" [disabled]="isLoadingX">
          <nb-icon icon="refresh-outline"></nb-icon>
          Refresh
        </button>
        <button
          class="btn btn-danger btn-sm"
          (click)="confirmGenerateZReport()"
          [disabled]="isGeneratingZ || isLoadingX">
          <nb-icon icon="lock-outline" class="me-1"></nb-icon>
          {{ isGeneratingZ ? 'Generating...' : 'Generate Z-Report (Close Period)' }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoadingX" class="text-center p-4">
      <nb-spinner size="large"></nb-spinner>
      <p class="mt-2 text-muted">Loading X-Report...</p>
    </div>

    <!-- Error -->
    <div *ngIf="!isLoadingX && xReportError" class="text-center p-4">
      <nb-icon icon="alert-circle-outline" class="text-danger" style="font-size: 2rem;"></nb-icon>
      <p class="mt-2 text-danger">{{ xReportError }}</p>
      <button class="btn btn-outline-secondary btn-sm" (click)="loadXReport()">Try Again</button>
    </div>

    <ng-container *ngIf="!isLoadingX && !xReportError && xReport">

      <!-- Grand Totals Summary -->
      <div class="row mb-4">
        <div class="col-md-3">
          <nb-card class="stat-card">
            <nb-card-body class="text-center">
              <div class="stat-value">{{ formatCurrency(xReport.grandTotalNet) }}</div>
              <div class="stat-label">Net Total</div>
            </nb-card-body>
          </nb-card>
        </div>
        <div class="col-md-3">
          <nb-card class="stat-card">
            <nb-card-body class="text-center">
              <div class="stat-value">{{ formatCurrency(xReport.grandTotalTax) }}</div>
              <div class="stat-label">Total Tax</div>
            </nb-card-body>
          </nb-card>
        </div>
        <div class="col-md-3">
          <nb-card class="stat-card highlight">
            <nb-card-body class="text-center">
              <div class="stat-value">{{ formatCurrency(xReport.grandTotalGross) }}</div>
              <div class="stat-label">Gross Total</div>
            </nb-card-body>
          </nb-card>
        </div>
        <div class="col-md-3">
          <nb-card class="stat-card">
            <nb-card-body class="text-center">
              <div class="stat-value">{{ xReport.transactionCount }}</div>
              <div class="stat-label">Transactions</div>
              <div class="text-muted small mt-1">
                {{ xReport.refundCount }} refund{{ xReport.refundCount !== 1 ? 's' : '' }}
                ({{ formatCurrency(xReport.refundTotal) }})
              </div>
            </nb-card-body>
          </nb-card>
        </div>
      </div>

      <div class="row">
        <!-- Sales by Payment Method -->
        <div class="col-md-4 mb-4">
          <nb-card>
            <nb-card-header>Sales by Payment Method</nb-card-header>
            <nb-card-body class="p-0">
              <table class="table table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Method</th>
                    <th class="text-center">Txns</th>
                    <th class="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of xReport.salesByPaymentMethod">
                    <td>{{ formatPaymentMethod(row.paymentMethod) }}</td>
                    <td class="text-center">{{ row.transactionCount }}</td>
                    <td class="text-end">{{ formatCurrency(row.totalAmount) }}</td>
                  </tr>
                  <tr *ngIf="!xReport.salesByPaymentMethod?.length">
                    <td colspan="3" class="text-center text-muted py-3">No data</td>
                  </tr>
                </tbody>
              </table>
            </nb-card-body>
          </nb-card>
        </div>

        <!-- Sales by Manager -->
        <div class="col-md-4 mb-4">
          <nb-card>
            <nb-card-header>Sales by Cashier</nb-card-header>
            <nb-card-body class="p-0">
              <table class="table table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Manager</th>
                    <th class="text-center">Txns</th>
                    <th class="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of xReport.salesByManager">
                    <td>{{ row.managerName || row.managerId }}</td>
                    <td class="text-center">{{ row.transactionCount }}</td>
                    <td class="text-end">{{ formatCurrency(row.totalAmount) }}</td>
                  </tr>
                  <tr *ngIf="!xReport.salesByManager?.length">
                    <td colspan="3" class="text-center text-muted py-3">No data</td>
                  </tr>
                </tbody>
              </table>
            </nb-card-body>
          </nb-card>
        </div>

        <!-- Tax Summary -->
        <div class="col-md-4 mb-4">
          <nb-card>
            <nb-card-header>Tax Summary</nb-card-header>
            <nb-card-body class="p-0">
              <table class="table table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Rate</th>
                    <th class="text-end">Net</th>
                    <th class="text-end">Tax</th>
                    <th class="text-end">Gross</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of xReport.taxSummary">
                    <td>{{ row.taxRate | number:'1.0-1' }}%</td>
                    <td class="text-end">{{ formatCurrency(row.netAmount) }}</td>
                    <td class="text-end">{{ formatCurrency(row.taxAmount) }}</td>
                    <td class="text-end">{{ formatCurrency(row.grossAmount) }}</td>
                  </tr>
                  <tr *ngIf="!xReport.taxSummary?.length">
                    <td colspan="4" class="text-center text-muted py-3">No data</td>
                  </tr>
                </tbody>
                <tfoot *ngIf="xReport.taxSummary?.length">
                  <tr class="table-active fw-bold">
                    <td>Total</td>
                    <td class="text-end">{{ formatCurrency(xReport.grandTotalNet) }}</td>
                    <td class="text-end">{{ formatCurrency(xReport.grandTotalTax) }}</td>
                    <td class="text-end">{{ formatCurrency(xReport.grandTotalGross) }}</td>
                  </tr>
                </tfoot>
              </table>
            </nb-card-body>
          </nb-card>
        </div>
      </div>

      <!-- Cash Management -->
      <nb-card class="mb-4">
        <nb-card-header>Cash Management</nb-card-header>
        <nb-card-body>
          <div class="row mb-3">
            <div class="col-md-3">
              <div class="cash-stat">
                <div class="cash-label">Opening Float</div>
                <div class="cash-value">{{ formatCurrency(xReport.openingFloat) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="cash-stat">
                <div class="cash-label">Closing Balance</div>
                <div class="cash-value">{{ formatCurrency(xReport.closingBalance) }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="cash-stat">
                <div class="cash-label">{{ 'FISCAL.CARD_CASHBACK' | translate }}</div>
                <div class="cash-value">{{ formatCardCashbackSummary(xReport) }}</div>
              </div>
            </div>
          </div>

          <div *ngIf="xReport.cashMovements?.length > 0">
            <h6 class="section-label">Cash Movements</h6>
            <table class="table table-sm table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Type</th>
                  <th class="text-end">Amount</th>
                  <th>Note</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let mv of xReport.cashMovements">
                  <td>
                    <span class="badge" [class.bg-success]="mv.type === 'in'" [class.bg-danger]="mv.type === 'out'" [class.bg-secondary]="mv.type !== 'in' && mv.type !== 'out'">
                      {{ mv.type }}
                    </span>
                  </td>
                  <td class="text-end">{{ formatCurrency(mv.amount) }}</td>
                  <td>{{ mv.note || '—' }}</td>
                  <td class="text-nowrap">{{ mv.timestamp | date:'dd/MM/yyyy HH:mm' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p *ngIf="!xReport.cashMovements?.length" class="text-muted fst-italic mb-0">No cash movements recorded.</p>
        </nb-card-body>
      </nb-card>

    </ng-container>

    <!-- Empty state when no report yet -->
    <div *ngIf="!isLoadingX && !xReportError && !xReport" class="text-center p-4">
      <nb-icon icon="file-text-outline" style="font-size: 2.5rem;" class="text-muted mb-2"></nb-icon>
      <h5>No data for this period</h5>
      <p class="text-muted">No transactions have been recorded in the current period.</p>
    </div>
  </div>

  <!-- ===================== Z-REPORTS SECTION ===================== -->
  <div *ngIf="activeSection === 'z-reports'">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-0">Z-Reports — Closed Periods</h5>
        <small class="text-muted">Immutable — each entry is a permanently closed accounting period</small>
      </div>
      <button class="btn btn-outline-secondary btn-sm" (click)="loadZReports()" [disabled]="isLoadingZ">
        <nb-icon icon="refresh-outline"></nb-icon>
        Refresh
      </button>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoadingZ" class="text-center p-4">
      <nb-spinner size="large"></nb-spinner>
      <p class="mt-2 text-muted">Loading Z-Reports...</p>
    </div>

    <!-- Error -->
    <div *ngIf="!isLoadingZ && zReportError" class="text-center p-4">
      <nb-icon icon="alert-circle-outline" class="text-danger" style="font-size: 2rem;"></nb-icon>
      <p class="mt-2 text-danger">{{ zReportError }}</p>
      <button class="btn btn-outline-secondary btn-sm" (click)="loadZReports()">Try Again</button>
    </div>

    <nb-card *ngIf="!isLoadingZ && !zReportError">
      <nb-card-body class="p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>Report #</th>
                <th>Period Start</th>
                <th>Period End</th>
                <th>Closed By</th>
                <th class="text-end">Gross Total</th>
                <th class="text-center">Transactions</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let report of zReports; trackBy: trackByReportId">
                <td class="font-monospace">{{ report.reportNumber }}</td>
                <td class="text-nowrap">{{ report.periodStart | date:'dd/MM/yyyy HH:mm' }}</td>
                <td class="text-nowrap">{{ report.periodEnd | date:'dd/MM/yyyy HH:mm' }}</td>
                <td>{{ report.closedByManagerName || '—' }}</td>
                <td class="text-end fw-bold">{{ formatCurrency(report.grandTotalGross) }}</td>
                <td class="text-center">{{ report.transactionCount }}</td>
                <td class="text-center">
                  <div class="d-flex gap-1 justify-content-center">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      (click)="viewZReport(report)"
                      nbTooltip="View full report">
                      <nb-icon icon="eye-outline"></nb-icon>
                      View
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      (click)="downloadSaft(report)"
                      nbTooltip="Download SAF-T XML">
                      <nb-icon icon="download-outline"></nb-icon>
                      SAF-T
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div *ngIf="!isLoadingZ && zReports.length === 0" class="text-center p-4">
          <nb-icon icon="archive-outline" style="font-size: 2.5rem;" class="text-muted mb-2"></nb-icon>
          <h5>No Z-Reports yet</h5>
          <p class="text-muted">No accounting periods have been closed. Use the X-Report tab to generate the first Z-Report.</p>
        </div>

        <!-- Pagination -->
        <div
          *ngIf="!isLoadingZ && zTotalPages > 1"
          class="d-flex justify-content-between align-items-center p-3 border-top">
          <span class="text-muted">
            Showing {{ (zCurrentPage - 1) * zPageSize + 1 }} to
            {{ Math.min(zCurrentPage * zPageSize, zTotal) }} of {{ zTotal }} reports
          </span>
          <div>
            <button
              class="btn btn-outline-secondary me-2"
              [disabled]="zCurrentPage === 1"
              (click)="onZPageChange(zCurrentPage - 1)">
              Previous
            </button>
            <span class="me-2">Page {{ zCurrentPage }} of {{ zTotalPages }}</span>
            <button
              class="btn btn-outline-secondary"
              [disabled]="zCurrentPage === zTotalPages"
              (click)="onZPageChange(zCurrentPage + 1)">
              Next
            </button>
          </div>
        </div>
      </nb-card-body>
    </nb-card>
  </div>
</div>

<!-- Z-Report Detail Modal -->
<nb-card *ngIf="showZDetail" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center no-print">
    <div class="d-flex align-items-center gap-3">
      <h5 class="mb-0">Z-Report #{{ selectedZReport?.reportNumber }}</h5>
      <span class="text-muted small">
        {{ selectedZReport?.periodStart | date:'dd/MM/yyyy HH:mm' }}
        — {{ selectedZReport?.periodEnd | date:'dd/MM/yyyy HH:mm' }}
      </span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-secondary" (click)="downloadSaft(selectedZReport)" *ngIf="selectedZReport">
        <nb-icon icon="download-outline"></nb-icon>
        Download SAF-T
      </button>
      <button class="btn btn-sm btn-outline-primary" (click)="printDetail()">
        <nb-icon icon="printer-outline"></nb-icon>
        Print
      </button>
      <button class="btn btn-ghost" (click)="closeZDetail()">
        <nb-icon icon="close-outline"></nb-icon>
      </button>
    </div>
  </nb-card-header>

  <nb-card-body class="modal-body">
    <div *ngIf="isLoadingZDetail" class="text-center p-4">
      <nb-spinner size="large"></nb-spinner>
      <p class="mt-2 text-muted">Loading report detail...</p>
    </div>

    <ng-container *ngIf="!isLoadingZDetail && selectedZReport">

      <!-- A. Totals Summary -->
      <div class="row mb-3">
        <div class="col-3">
          <div class="stat-card-sm">
            <div class="stat-value-sm">{{ formatCurrency(selectedZReport.grandTotalNet) }}</div>
            <div class="stat-label">Net Total</div>
          </div>
        </div>
        <div class="col-3">
          <div class="stat-card-sm">
            <div class="stat-value-sm">{{ formatCurrency(selectedZReport.grandTotalTax) }}</div>
            <div class="stat-label">Total Tax</div>
          </div>
        </div>
        <div class="col-3">
          <div class="stat-card-sm highlight">
            <div class="stat-value-sm">{{ formatCurrency(selectedZReport.grandTotalGross) }}</div>
            <div class="stat-label">Gross Total</div>
          </div>
        </div>
        <div class="col-3">
          <div class="stat-card-sm">
            <div class="stat-value-sm">{{ selectedZReport.transactionCount ?? 0 }}</div>
            <div class="stat-label">Transactions</div>
          </div>
        </div>
      </div>

      <!-- B. Summary stats row (Finansrapport KPIs) -->
      <div class="row mb-3">
        <div class="col-md-4">
          <div class="stat-card-sm">
            <div class="stat-value-sm">{{ selectedZReport.uniqueCustomerCount ?? 0 }}</div>
            <div class="stat-label">Antal kunder</div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="stat-card-sm">
            <div class="stat-value-sm">{{ formatCurrency(getAverageOrderValue()) }}</div>
            <div class="stat-label">Gns. pr. kunde</div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="stat-card-sm">
            <div class="stat-value-sm">{{ selectedZReport.totalItemsCount ?? 0 }}</div>
            <div class="stat-label">Antal varer</div>
          </div>
        </div>
      </div>

      <!-- C+D. Transaction Breakdown + Payment Method (side by side) -->
      <div class="row mb-3">

        <!-- C. Transaction type breakdown -->
        <div class="col-md-4 mb-3">
          <h6 class="section-label">Transaction Breakdown</h6>
          <table class="table table-sm table-bordered">
            <thead class="table-light">
              <tr><th>Type</th><th class="text-center">Antal</th><th class="text-end">Beløb</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Slet bon</td>
                <td class="text-center">{{ selectedZReport.voidedCount ?? 0 }}</td>
                <td class="text-end">{{ formatCurrency(selectedZReport.voidedTotal ?? 0) }}</td>
              </tr>
              <tr>
                <td>Varesalg retur</td>
                <td class="text-center">{{ selectedZReport.returnCount ?? (selectedZReport.refundCount ?? 0) }}</td>
                <td class="text-end">{{ formatCurrency(selectedZReport.returnTotal ?? (selectedZReport.refundTotal ?? 0)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- D. Sales by Payment Method (enhanced with return cols) -->
        <div class="col-md-8 mb-3">
          <h6 class="section-label">Sales by Payment Method</h6>
          <table class="table table-sm table-bordered">
            <thead class="table-light">
              <tr>
                <th>Method</th>
                <th class="text-center">Antal</th>
                <th class="text-end">Registreret</th>
                <th class="text-center">Retur antal</th>
                <th class="text-end">Retur beløb</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of selectedZReport.salesByPaymentMethod">
                <td>{{ formatPaymentMethod(row.paymentMethod) }}</td>
                <td class="text-center">{{ row.transactionCount }}</td>
                <td class="text-end">{{ formatCurrency(row.totalAmount) }}</td>
                <td class="text-center">{{ row.returnCount ?? 0 }}</td>
                <td class="text-end">{{ formatCurrency(row.returnTotal ?? 0) }}</td>
              </tr>
              <tr *ngIf="!selectedZReport.salesByPaymentMethod?.length">
                <td colspan="5" class="text-muted text-center">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- E. Currency breakdown (shown only when multi-currency or foreign currency) -->
      <div class="row mb-3" *ngIf="selectedZReport.salesByCurrency?.length > 1 || hasForeignCurrency()">
        <div class="col-md-6">
          <h6 class="section-label">Currency Breakdown</h6>
          <table class="table table-sm table-bordered">
            <thead class="table-light">
              <tr><th>Currency</th><th class="text-center">Antal</th><th class="text-end">Beløb</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of selectedZReport.salesByCurrency">
                <td>{{ row.currency }}</td>
                <td class="text-center">{{ row.transactionCount }}</td>
                <td class="text-end">{{ formatCurrency(row.totalAmount, row.currency) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- G. Sales by Cashier + Tax Summary -->
      <div class="row mb-3">
        <div class="col-md-6 mb-3">
          <h6 class="section-label">Sales by Cashier</h6>
          <table class="table table-sm table-bordered">
            <thead class="table-light">
              <tr><th>Manager</th><th class="text-center">Txns</th><th class="text-end">Total</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of selectedZReport.salesByManager">
                <td>{{ row.managerName || row.managerId }}</td>
                <td class="text-center">{{ row.transactionCount }}</td>
                <td class="text-end">{{ formatCurrency(row.totalAmount) }}</td>
              </tr>
              <tr *ngIf="!selectedZReport.salesByManager?.length">
                <td colspan="3" class="text-muted text-center">—</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="col-md-6 mb-3">
          <h6 class="section-label">Tax Summary</h6>
          <table class="table table-sm table-bordered">
            <thead class="table-light">
              <tr><th>Rate</th><th class="text-end">Net</th><th class="text-end">Tax</th><th class="text-end">Gross</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of selectedZReport.taxSummary">
                <td>{{ row.taxRate | number:'1.0-1' }}%</td>
                <td class="text-end">{{ formatCurrency(row.netAmount) }}</td>
                <td class="text-end">{{ formatCurrency(row.taxAmount) }}</td>
                <td class="text-end">{{ formatCurrency(row.grossAmount) }}</td>
              </tr>
              <tr *ngIf="!selectedZReport.taxSummary?.length">
                <td colspan="4" class="text-muted text-center">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Cash Management stats -->
      <div class="row mb-3">
        <div class="col-md-3">
          <div class="cash-stat">
            <div class="cash-label">Opening Float</div>
            <div class="cash-value">{{ formatCurrency(selectedZReport.openingFloat) }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="cash-stat">
            <div class="cash-label">Closing Balance</div>
            <div class="cash-value">{{ formatCurrency(selectedZReport.closingBalance) }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="cash-stat">
            <div class="cash-label">{{ 'FISCAL.CARD_CASHBACK' | translate }}</div>
            <div class="cash-value">{{ formatCardCashbackSummary(selectedZReport) }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="cash-stat">
            <div class="cash-label">Refunds</div>
            <div class="cash-value text-danger">
              {{ selectedZReport.returnCount ?? (selectedZReport.refundCount ?? 0) }}
              ({{ formatCurrency(selectedZReport.returnTotal ?? (selectedZReport.refundTotal ?? 0)) }})
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="cash-stat">
            <div class="cash-label">Closed By</div>
            <div class="cash-value">{{ selectedZReport.closedByManagerName || '—' }}</div>
          </div>
        </div>
      </div>

      <!-- F. Cash Movements -->
      <div *ngIf="selectedZReport.cashMovements?.length > 0" class="mb-3">
        <h6 class="section-label">Cash Movements</h6>
        <table class="table table-sm table-bordered">
          <thead class="table-light">
            <tr>
              <th>Type</th>
              <th class="text-end">Amount</th>
              <th>Note</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let mv of selectedZReport.cashMovements">
              <td>
                <span class="badge"
                  [class.bg-success]="mv.type === 'in'"
                  [class.bg-danger]="mv.type === 'out'"
                  [class.bg-secondary]="mv.type !== 'in' && mv.type !== 'out'">
                  {{ mv.type }}
                </span>
              </td>
              <td class="text-end">{{ formatCurrency(mv.amount) }}</td>
              <td>{{ mv.note || '—' }}</td>
              <td class="text-nowrap">{{ mv.timestamp | date:'dd/MM/yyyy HH:mm' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </ng-container>
  </nb-card-body>
</nb-card>
